import { EYE_H, EYE_SPLIT, EYE_W, REST_GAZE, type HeadGaze } from './face'
import { clamp, easings, lerp } from './math'
import type { EyeCfg } from './states'

/**
 * Bot resting expression.
 *
 * Vocabulary follows robo-eye grids: open pills at rest, ^^ laugh arcs, ∨ sad arcs,
 * flat dashes for bored, downward ∪ curves for sleep, inward slants for angry/worried,
 * cross-tilt for dizzy, asymmetric dash+square for skeptical.
 *
 * The face is only two capsules, so everything hinges on four levers: head
 * orientation, eye spacing, their proportions, and each eye's own tilt. That last
 * one enables anger and sadness: they need MIRRORED tilts (tops converging or
 * diverging), impossible with head roll alone, which tilts both eyes the same way.
 *
 * Only the resting state carries this expression. Expressive video states (wink,
 * wide eyes, notification) keep their own: that's what we came to reproduce.
 */
/** Enumerated so the i18n layer verifies translations at compile time. */
export type ExpressionId =
  | 'neutre'
  | 'attentif'
  | 'surpris'
  | 'excite'
  | 'heureux'
  | 'hilare'
  | 'colere'
  | 'triste'
  | 'effraye'
  | 'mefiant'
  | 'confus'
  | 'curieux'
  | 'fier'
  | 'timide'
  | 'blase'
  | 'somnolent'

export interface BotExpression {
  id: ExpressionId
  gaze: HeadGaze
  split: number
  eyes: [EyeCfg, EyeCfg]
}

/** `tilt` in degrees, positive = top of the capsule goes to the right. */
const eye = (
  w: number,
  h: number,
  tilt = 0,
  open = 1,
  form: EyeCfg['form'] = 'capsule'
): EyeCfg => ({ w, h, tilt, open, form })

const smilePair = (w: number, h: number, open = 1): [EyeCfg, EyeCfg] => [
  eye(w, h, 0, open, 'smile'),
  eye(w, h, 0, open, 'smile')
]

/** Both eyes identical, mirrored tilts when `tilt` is provided. */
const pair = (w: number, h: number, tilt = 0, open = 1): [EyeCfg, EyeCfg] => [
  eye(w, h, tilt, open),
  eye(w, h, -tilt, open)
]

export const EXPRESSIONS: BotExpression[] = [
  {
    // pose measured frame-by-frame from the reference video
    id: 'neutre',
    gaze: { ...REST_GAZE },
    split: EYE_SPLIT,
    eyes: [eye(EYE_W, EYE_H), eye(EYE_W, EYE_H)]
  },
  {
    // robo grid « worried » — tall squares, tops angle inward
    id: 'attentif',
    gaze: { yaw: 4, pitch: 3, roll: -4 },
    split: 16,
    eyes: pair(0.26, 0.4, 9)
  },
  {
    // tall round ovals — robo « surprised »
    id: 'surpris',
    gaze: { yaw: 3, pitch: -3, roll: 0 },
    split: 19.5,
    eyes: pair(0.48, 0.5)
  },
  {
    // wide open smile-eyes — dome top, cheek pinch; energy without angry ∧ slashes
    id: 'excite',
    gaze: { yaw: 6, pitch: 10, roll: 0 },
    split: 19.5,
    eyes: smilePair(0.42, 0.4)
  },
  {
    // « smiling with your eyes » — rounded top, bottom curves in (not a tilted oval)
    id: 'heureux',
    gaze: { yaw: 5, pitch: 12, roll: 0 },
    split: 17.5,
    eyes: smilePair(0.3, 0.38)
  },
  {
    // same smile-eye shape, pinched shut; `laughPulse` bobs the squeeze
    id: 'hilare',
    gaze: { yaw: 4, pitch: 14, roll: 0 },
    split: 18,
    eyes: smilePair(0.32, 0.34, 0.52)
  },
  {
    // inward ∧ slashes — robo « angry » (flat, converging; opposite of sad)
    id: 'colere',
    gaze: { yaw: 3, pitch: 5, roll: 0 },
    split: 17,
    eyes: pair(0.4, 0.1, 38)
  },
  {
    // tall ∨ droop — robo « sad » (open, outer lids down; never a flat slash)
    id: 'triste',
    gaze: { yaw: 2, pitch: -18, roll: -2 },
    split: 15.5,
    eyes: pair(0.21, 0.48, -16)
  },
  {
    // robo grid « worried / shocked » — big squares, slight inward pitch
    id: 'effraye',
    gaze: { yaw: 2, pitch: -18, roll: 0 },
    split: 20.5,
    eyes: pair(0.46, 0.58, 7)
  },
  {
    // robo grid « skeptical » — dash + square
    id: 'mefiant',
    gaze: { yaw: 12, pitch: 6, roll: -6 },
    split: 16,
    eyes: [eye(0.2, 0.08), eye(0.24, 0.42)]
  },
  {
    // robo grid « dizzy / cross-eyed » — both lids lean toward the nose
    id: 'confus',
    gaze: { yaw: -10, pitch: 3, roll: 6 },
    split: 15.5,
    eyes: [eye(0.23, 0.39, 14), eye(0.23, 0.39, 14)]
  },
  {
    id: 'curieux',
    gaze: { yaw: 16, pitch: -9, roll: -15 },
    split: 16.5,
    eyes: [eye(0.24, 0.46, -8), eye(0.2, 0.38, -8)]
  },
  {
    // robo grid « sinister / determined » — downward-slanted semi-ovals
    id: 'fier',
    gaze: { yaw: 5, pitch: 14, roll: 0 },
    split: 17,
    eyes: pair(0.34, 0.12, 24)
  },
  {
    // small peeking pills
    id: 'timide',
    gaze: { yaw: -19, pitch: -14, roll: -7 },
    split: 14,
    eyes: pair(0.15, 0.34)
  },
  {
    // robo grid « bored / neutral » — flat horizontal dashes (not sleep)
    id: 'blase',
    gaze: { yaw: -22, pitch: 2, roll: 0 },
    split: 16,
    eyes: pair(0.38, 0.09)
  },
  {
    // robo grid « sleeping / peaceful » — closed downward ∪ curves
    id: 'somnolent',
    gaze: { yaw: 6, pitch: -8, roll: -3 },
    split: 16,
    eyes: pair(0.34, 0.095, -11, 0.06)
  }
]

export const EXPRESSION_BY_ID = new Map<string, BotExpression>(EXPRESSIONS.map((e) => [e.id, e]))
export const DEFAULT_EXPRESSION = 'neutre'

/** Minimum dwell between pointer moods — must exceed `SHAPE_MORPH`. */
export const HUMEUR_MIN_MS = 2800

const lerpEyeCfg = (a: EyeCfg, b: EyeCfg, t: number): EyeCfg => ({
  w: lerp(a.w, b.w, t),
  h: lerp(a.h, b.h, t),
  tilt: lerp(a.tilt ?? 0, b.tilt ?? 0, t),
  open: lerp(a.open, b.open, t),
  form: t >= 0.5 ? (b.form ?? 'capsule') : (a.form ?? 'capsule')
})

/** Interpolation of two expressions: the change slides rather than jumps. */
export function blendExpression(a: BotExpression, b: BotExpression, t: number): BotExpression {
  return {
    id: b.id,
    gaze: {
      yaw: lerp(a.gaze.yaw, b.gaze.yaw, t),
      pitch: lerp(a.gaze.pitch, b.gaze.pitch, t),
      roll: lerp(a.gaze.roll, b.gaze.roll, t)
    },
    split: lerp(a.split, b.split, t),
    eyes: [lerpEyeCfg(a.eyes[0], b.eyes[0], t), lerpEyeCfg(a.eyes[1], b.eyes[1], t)]
  }
}

export function exprDistance(a: BotExpression, b: BotExpression): number {
  let d = 0
  d += Math.abs(a.split - b.split) / 10
  d += Math.abs(a.gaze.yaw - b.gaze.yaw) / 30
  d += Math.abs(a.gaze.pitch - b.gaze.pitch) / 30
  d += Math.abs(a.gaze.roll - b.gaze.roll) / 20
  for (let i = 0; i < 2; i++) {
    const ea = a.eyes[i]!
    const eb = b.eyes[i]!
    d += Math.abs(ea.w - eb.w) / 0.3
    d += Math.abs(ea.h - eb.h) / 0.4
    d += Math.abs((ea.tilt ?? 0) - (eb.tilt ?? 0)) / 40
    d += Math.abs((ea.open ?? 1) - (eb.open ?? 1)) / 0.5
  }
  return d / 8
}

/** Forced blink peak — `0.2 s` lid, closed at `0.1 s` in `engine.ts`. */
export const EXPR_BLINK_PEAK = 0.1

export const EXPR_MORPH_MIN = 0.34
export const EXPR_MORPH_MAX = 0.45
const EXPR_MORPH_BASE = 0.38

export function exprMorphDuration(a: BotExpression, b: BotExpression): number {
  const d = exprDistance(a, b)
  return clamp(EXPR_MORPH_BASE + d * 0.09, EXPR_MORPH_MIN, EXPR_MORPH_MAX)
}

/**
 * Staged blend: brief hold (anticipation), then ease-out through the blink window.
 * Monotone — safe for the eyefit offset table on the circle.
 */
export function exprMorphBlendT(k: number, duration: number, _distance: number): number {
  if (k >= 1) return 1
  if (k <= 0) return 0
  const hold = (EXPR_BLINK_PEAK / duration) * 0.42
  if (k < hold) return 0
  return easings.easeOutQuint(clamp((k - hold) / (1 - hold)))
}
